import { useState } from 'react';
import { Copy, Check, FileText } from 'lucide-react';
import { formatCrypto, formatUSD, truncateHash } from '../../utils/formatAmount';
import { TokenChainLogo } from './CryptoLogo';
import { chainDisplayLabel } from '../../utils/assetLogos';

/**
 * The "recent" tables shown on Overview and Balance.
 *
 * One component for both pages, so the two cannot drift apart: the Overview
 * transactions table was the design, and Balance used to render a different,
 * columnless list of the same data. Recent invoices use the same table shape
 * (status dot, mono id, right-aligned amount and date).
 *
 * Styles are injected once from here and scoped to the `rtx-` prefix.
 */

// Tone keys match .rd-badge's vocabulary: ok / err / info / warn / plain.
export const statusTone = (status) => {
  switch (status?.toLowerCase()) {
    case 'succeeded':
    case 'paid':
    case 'overpaid':
      return 'ok';
    case 'failed':
    case 'overdue':
    case 'cancelled':
      return 'err';
    case 'partially_paid':
      return 'info';
    case 'pending':
    case 'created':
    case 'open':
      return 'warn';
    default:
      return 'plain';
  }
};

const formatTxDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDay = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

// What the payment was raised against. Mirrors the transactions page's TYPE
// column so a row reads the same in both places.
const txTypeOf = (tx) => {
  const type = String(tx?.type || '').toLowerCase();
  if (type === 'payout') return { label: 'Payout', tone: 'out' };
  if (type === 'refund') return { label: 'Refund', tone: 'refund' };
  if (type === 'conversion' || type === 'conversion_debit') return { label: 'Conversion', tone: 'neutral' };
  if (tx?.invoiceId) return { label: 'Invoice', tone: 'in' };
  if (tx?.sessionId || tx?.paymentLinkId) return { label: 'Order', tone: 'in' };
  return { label: 'Untracked', tone: 'untracked' };
};

// The stored value is a database enum; printed raw it reads as a column name.
const TX_STATUS_LABELS = {
  succeeded: 'Succeeded',
  pending: 'Pending',
  processing: 'Processing',
  failed: 'Failed',
  refunded: 'Refunded',
  disputed: 'Disputed',
  uncaptured: 'Uncaptured',
  not_supported_token: 'Unsupported token',
  partially_paid: 'Partially paid',
};

const INVOICE_STATUS_LABELS = {
  paid: 'Paid',
  overpaid: 'Overpaid',
  open: 'Open',
  pending: 'Pending',
  partially_paid: 'Partially paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  draft: 'Draft',
};

const labelFor = (labels, raw) => {
  const key = String(raw ?? '').toLowerCase();
  return labels[key] || key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) || '—';
};

const StatusDot = ({ status, labels }) => (
  <span className={`rtx-status ${statusTone(status)}`} title={status}>
    <i aria-hidden="true" />
    {labelFor(labels, status)}
  </span>
);

const TransactionRow = ({ transaction, onCopy, copied }) => {
  const chain = transaction.network || transaction.networkName;
  const kind = txTypeOf(transaction);

  // When there is no USD figure the token amount is promoted rather than a
  // placeholder being invented.
  const hasUsd = transaction.amountUsd > 0;
  const tokenAmount = `${formatCrypto(transaction.amount, 2)} ${transaction.currency}`;
  const hash = transaction.txHash || transaction.transactionId;

  return (
    <tr className="rtx-row">
      <td>
        <StatusDot status={transaction.status} labels={TX_STATUS_LABELS} />
      </td>
      <td>
        <span className="rtx-hash">
          <span className="rtx-hash__text">{hash ? truncateHash(hash, 6, 4) : '—'}</span>
          {hash && (
            <button
              type="button"
              className="rtx-copy"
              onClick={() => onCopy(hash)}
              title="Copy"
              aria-label={`Copy ${hash}`}
            >
              {copied === hash ? <Check size={12} /> : <Copy size={12} />}
            </button>
          )}
        </span>
      </td>
      <td>
        <span className={`rtx-type rtx-type--${kind.tone}`}>{kind.label}</span>
      </td>
      <td>
        <span className="rtx-asset">
          <TokenChainLogo symbol={transaction.currency} chain={chain} size={26} />
          <span className="rtx-asset__text">
            <span className="rtx-asset__sym">{transaction.currency}</span>
            <span className="rtx-asset__chain">{chainDisplayLabel(chain) || chain}</span>
          </span>
        </span>
      </td>
      <td className="rtx-num">
        <span className="rtx-amount">
          {hasUsd ? `$${formatUSD(transaction.amountUsd)}` : tokenAmount}
        </span>
        {hasUsd && <span className="rtx-amount__sub">{tokenAmount}</span>}
      </td>
      <td className="rtx-num rtx-date">{formatTxDate(transaction.createdAt)}</td>
    </tr>
  );
};

/** Status | Transaction | Type | Asset | Amount | Date */
export const RecentTransactionsTable = ({ transactions }) => {
  const [copied, setCopied] = useState(null);
  const handleCopy = async (hash) => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(hash);
      setTimeout(() => setCopied((c) => (c === hash ? null : c)), 1500);
    } catch {
      /* clipboard unavailable: nothing to show */
    }
  };

  return (
    <>
      <table className="rtx-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Transaction</th>
            <th>Type</th>
            <th>Asset</th>
            <th className="rtx-num">Amount</th>
            <th className="rtx-num">Date</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((t) => (
            <TransactionRow
              key={t.transactionId || t.id}
              transaction={t}
              onCopy={handleCopy}
              copied={copied}
            />
          ))}
        </tbody>
      </table>
      <style>{styles}</style>
    </>
  );
};

/** Status | Invoice | Amount | Date — rows open the invoice. */
export const RecentInvoicesTable = ({ invoices, onOpen }) => (
  <>
    <table className="rtx-table rtx-table--clickable">
      <thead>
        <tr>
          <th>Status</th>
          <th>Invoice</th>
          <th className="rtx-num">Amount</th>
          <th className="rtx-num">Date</th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((invoice) => (
          <tr
            key={invoice.invoiceId}
            className="rtx-row"
            onClick={() => onOpen(invoice.invoiceId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(invoice.invoiceId);
              }
            }}
            tabIndex={0}
            role="link"
            aria-label={`Open invoice ${invoice.invoiceId}`}
          >
            <td>
              <StatusDot status={invoice.status} labels={INVOICE_STATUS_LABELS} />
            </td>
            <td>
              <span className="rtx-inv">
                <span className="rtx-inv__icon" aria-hidden="true">
                  <FileText size={15} />
                </span>
                <span className="rtx-asset__text">
                  <span className="rtx-asset__sym rtx-inv__name">{invoice.customerName || '—'}</span>
                  <span className="rtx-hash__text rtx-inv__id">{invoice.invoiceId}</span>
                </span>
              </span>
            </td>
            <td className="rtx-num">
              <span className="rtx-amount">${formatUSD(invoice.amount)}</span>
            </td>
            <td className="rtx-num rtx-date">
              {formatTxDate(invoice.createdAt)}
              {/* The due date still matters (the Overdue tab is about it), so
                  it rides under the created date rather than replacing it. */}
              {invoice.dueDate && (
                <span className="rtx-amount__sub">Due {formatDay(invoice.dueDate)}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <style>{styles}</style>
  </>
);

const styles = `
  .rtx-table { width: 100%; border-collapse: collapse; }
  .rtx-table thead th {
    text-align: left;
    padding: 8px 12px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--t-500, #6c7189);
    border-bottom: 1px solid var(--line, #eaebf3);
    white-space: nowrap;
  }
  .rtx-table .rtx-num { text-align: right; }
  .rtx-table tbody td {
    padding: 10px 12px;
    font-size: 13px;
    color: var(--ink, #0e1124);
    border-bottom: 1px solid var(--line, #eaebf3);
    vertical-align: middle;
  }
  .rtx-table tbody tr:last-child td { border-bottom: none; }
  .rtx-row { transition: background 0.14s ease; }
  /* A 40%-alpha tint rather than --bg-inset, which is the Type pills' fill and
     made them vanish into the row on hover. */
  .rtx-row:hover { background: rgba(109, 93, 246, 0.04); }
  .rtx-table--clickable .rtx-row { cursor: pointer; }
  .rtx-table--clickable .rtx-row:focus-visible { outline: 2px solid var(--brand-400, #a99cff); outline-offset: -2px; }

  /* status: a dot plus the word, so colour is never the only signal */
  .rtx-status {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 12.5px; font-weight: 600; white-space: nowrap;
  }
  .rtx-status i {
    width: 8px; height: 8px; border-radius: 50%; flex: none;
    background: var(--t-400, #9aa0b6);
  }
  .rtx-status.ok i    { background: var(--ok, #0fb981); }
  .rtx-status.warn i  { background: var(--warn, #f5a524); }
  .rtx-status.err i   { background: var(--err, #f4476b); }
  .rtx-status.info i  { background: var(--info, #3b9eff); }
  .rtx-status.plain i { background: var(--t-400, #9aa0b6); }

  .rtx-hash { display: inline-flex; align-items: center; gap: 6px; }
  .rtx-hash__text { font-family: var(--font-mono); font-size: 12px; color: var(--t-700, #3b4059); }
  .rtx-copy {
    display: inline-grid; place-items: center;
    width: 20px; height: 20px; border: none; background: none;
    border-radius: 6px; color: var(--t-400, #9aa0b6); cursor: pointer;
    transition: background 0.14s, color 0.14s;
  }
  .rtx-copy:hover { background: var(--brand-100, #ece9ff); color: var(--brand-700, #5847d6); }

  .rtx-type {
    display: inline-block; padding: 3px 9px; border-radius: 999px;
    font-size: 11.5px; font-weight: 600; white-space: nowrap;
  }
  .rtx-type--in       { background: var(--brand-100, #ece9ff); color: var(--brand-700, #5847d6); }
  .rtx-type--out      { background: var(--info-soft, #e8f2ff); color: var(--info-ink, #1c73d6); }
  .rtx-type--refund   { background: var(--err-soft, #fdebef); color: var(--err-ink, #ce2649); }
  .rtx-type--untracked{ background: var(--warn-soft, #fef3e0); color: var(--warn-ink, #b9760a); }
  .rtx-type--neutral  { background: var(--bg-inset, #eff0f8); color: var(--t-500, #6c7189); }

  .rtx-asset { display: inline-flex; align-items: center; gap: 9px; min-width: 0; }
  .rtx-asset__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .rtx-asset__sym { font-size: 12.5px; font-weight: 600; }
  .rtx-asset__chain { font-size: 11px; color: var(--t-500, #6c7189); white-space: nowrap; }

  .rtx-amount { display: block; font-size: 13.5px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .rtx-amount__sub { display: block; font-size: 11px; color: var(--t-500, #6c7189); font-variant-numeric: tabular-nums; }
  .rtx-date { font-size: 12px; color: var(--t-500, #6c7189); white-space: nowrap; }

  /* Invoice cell: the document tile, then customer over invoice id. */
  .rtx-inv { display: inline-flex; align-items: center; gap: 9px; min-width: 0; }
  .rtx-inv__icon {
    flex: none; display: grid; place-items: center;
    width: 26px; height: 26px; border-radius: 8px;
    background: var(--brand-100, #ece9ff); color: var(--brand-700, #5847d6);
  }
  .rtx-inv__name { max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .rtx-inv__id { font-size: 11px; color: var(--t-500, #6c7189); }
`;
